import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend } = clients.direct;

// Surface the backend's own message - the tax district guard rejects the save and names the
// field, which the generic text would hide behind "there was a problem".
const gqlErrorMessage = (error) => (error && error.graphQLErrors && error.graphQLErrors[0]
  && error.graphQLErrors[0].message) || (error && error.message);
const defaultTransactionTaxesApportionmentFields = {
  _id: '',
  fromDate: '',
  toDate: '',
  taxYear: '',
  schoolDistrict: '',
  taxAmt: '',
  penaltyAmount: '',
  totalFees: '',
  totalAmount: '',
  description: '',
  isLocked: '',
  isExempt: '',
  grossAssessed: '',
  isTrialRecord: '',
  isNormalRecord: '',
  isManualRecord: '',
  highlightRow: '',
  businessDate: '',
  skipTIF: '',
};

export const apportionmentTaxes = {
  namespaced: true,
  state: () => ({
    selectedItems: [],
    totalLockedRecordsCount: 0,
    unpostedRecords: {
      alert: '',
      unPostedDates: [],
    },
  }),
  mutations: {
    setSelectedItems(state, items) {
      state.selectedItems = items;
    },
    setTotalLockedRecordsCount(state, items) {
      state.totalLockedRecordsCount = items;
    },
    setUnpostedRecords(state, unpostedRecords) {
      state.unpostedRecords = unpostedRecords;
    },
  },
  actions: {
    async loadapportionmenttaxesData({ commit, dispatch }, {
      criteria, fields, baseCrudKey, postProcessTrans,
    }) {
      let results;
      try {
        const gqlfields = fields || defaultTransactionTaxesApportionmentFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query apportionmentTaxes($criteria: apportionmentTaxesCriteriaType) {
              apportionmentTaxes(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)},
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        if (baseCrudKey && results.data && results.data.apportionmentTaxes) {
          const apportionmentData = postProcessTrans
            ? postProcessTrans(results.data.apportionmentTaxes) || []
            : results.data.apportionmentTaxes || [];
          commit('base/crud/setItems', [baseCrudKey, apportionmentData], { root: true });
        }
        return results.data.apportionmentTaxes;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting Apportionment transactions list.', { root: true });
        return [];
      }
    },
    async apportionmenttaxesTotals({ commit, dispatch }, {
      criteria,
    }) {
      let results;
      try {
        const gqlfields = {
          totalTaxAmount: '',
          totalPenalty: '',
          totalFees: '',
          totalofAll: '',
        };
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query getTotals($criteria: apportionmentTaxesCriteriaType) {
              getTotals(criteria: $criteria) {
                ${fieldsToGqlString(gqlfields)},
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        return results.data.getTotals || [];
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting payment transactions list.', { root: true });
        return [];
      }
    },
    async taxesApportionmnetCollectRecords({ dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query
          taxesApportionmentgetInfo ($criteria: apportionmentTaxesCriteriaType) {
            taxesApportionmentgetInfo(criteria: $criteria) {
              status,
              message,
              negativeCollections {
                taxYear,
                schoolDistrict,
                taxAmt,
              },
            },
          }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        const result = results.data ? (results.data.taxesApportionmentgetInfo || {}) : {};
        // the server refuses a second Build Records while one is already running
        if (result.status === 'already_running') {
          alert(result.message);
        }
        if (result.status === 'overlapping_dates found') {
          alert(result.message || 'Overlapping dates found. Please review the records.');
        }
        if (result.status === 'ok') {
          dispatch('flashSuccess', 'build records done Successfully', { root: true });
        }
        return result;
      } catch (e) {
        // Check if this is a GL fund validation error
        const errorMessage = e.message || '';
        const isGLFundError = errorMessage.includes('Section field, GL fund missing in the record')
          || errorMessage.includes('VALIDATION_ERROR');

        if (isGLFundError) {
          // Extract record data if available
          let errorData = null;
          let headline = null;
          try {
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
              const gqlError = e.graphQLErrors[0];
              if (gqlError.message) {
                const parsedMessage = JSON.parse(gqlError.message);
                // Handle new structure with invalidRecords or fallback to old recordData
                errorData = parsedMessage.invalidRecords ? parsedMessage : parsedMessage.recordData;
                headline = parsedMessage.message;
              }
            }
          } catch (parseError) {
            console.warn('Could not parse error record data:', parseError);
          }

          // Throw a specific error that the component can catch
          const glFundError = new Error('VALIDATION_ERROR');
          glFundError.recordData = errorData;
          glFundError.headline = headline;
          glFundError.originalError = e;
          throw glFundError;
        }

        // dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There is a problem while getting records.', { root: true });
        throw e; // Re-throw the error so the component can handle it
      }
    },
    async upsertTaxApportionmentRecord({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || defaultTransactionTaxesApportionmentFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxApportionment ($item: apportionmentTaxesInputType!)  {
                upsertTaxApportionment(upsert: $item) {
                    ${fieldsToGqlString(gqlfields)},
                  }
                }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const taxApportionmentTransStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newTransaction = results.data ? (results.data.upsertTaxApportionment
          || {}) : {};
        const newList = mergeByIdNewAtTop(taxApportionmentTransStateData || [], newTransaction);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        return newTransaction;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', gqlErrorMessage(e) || 'There was a problem in upserting the transaction!', { root: true });
      }
      return {};
    },

    async upsertTaxApportionmentInBulk({ dispatch }, { item, fields }) {
      try {
        const gqlfields = fields || defaultTransactionTaxesApportionmentFields;
        await dispatch('reAuth', null, { root: true });
        await backend.mutate({
          mutation: gql`mutation upsertTaxApportionmentInBulk($item:[apportionmentTaxesInputType]) {
            upsertTaxApportionmentInBulk(upserts:$item) {
              ${(fieldsToGqlString(gqlfields))}
            }
          }`,
          variables: {
            item,
          },
        });
        dispatch('flashSuccess', 'Succesfully upserting the Tax Apportionment transaction!', { root: true });
      } catch (error) {
        dispatch('logException', error, { root: true });
        dispatch('flashError', gqlErrorMessage(error) || 'There was a problem while upserting the Tax Apportionment transaction!', { root: true });
      }
    },

    async deleteApportionmnetaxesRecord({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deletetaxapportionmnettaxes ($id: ID!)  {
            deletetaxapportionmnettaxes(id: $id) {
                  _id
                }
              }`,
          variables: {
            id: item._id,
          },
        });
        clients.handleGQLErrors(results, commit);
        const delId = results.data ? (results.data.deleteSaiMonthlySummary || {})._id : null;
        const apportionmentTransData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = apportionmentTransData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        return delId;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while deleting the transaction!', { root: true });
      }
      return null;
    },
    async taxesApportionmnetCountForLockedRecords({ commit, dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query
          countLockedRecords ($criteria: apportionmentTaxesCriteriaType) {
            countLockedRecords(criteria: $criteria) {
              totalLockedRecordsResult
            },
          }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const result = results.data ? (results.data.countLockedRecords[0] || 0) : {};
        commit('setTotalLockedRecordsCount', result.totalLockedRecordsResult);
        return result;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There is a problem while getting count.', { root: true });
      }
      return null;
    },
    async taxesApportionmnetCheckForUnPostedRecords({ commit, dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });

        results = await backend.query({
          query: gql`
            query checkForUnpostedRecords($criteria: apportionmentTaxesCriteriaType!) {
              checkForUnpostedRecords(criteria: $criteria) {
                alert
                unPostedDates
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });

        clients.handleGQLErrors(results, commit);

        const result = results.data ? results.data.checkForUnpostedRecords : {};
        commit('setUnpostedRecords', result);
        return result;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while retrieving unposted records.', { root: true });
      }
      return null;
    },
  },

};

export default apportionmentTaxes;
